import React, { useState, useCallback, useEffect, useRef } from 'react';
import styles from './Minesweeper.module.css';

type CellState = 'hidden' | 'revealed' | 'flagged' | 'question';
type GameState = 'playing' | 'won' | 'lost';

interface Cell {
  isMine: boolean;
  adjacentMines: number;
  state: CellState;
}

type Difficulty = 'beginner' | 'intermediate' | 'expert';

const DIFFICULTIES: Record<Difficulty, { rows: number; cols: number; mines: number }> = {
  beginner: { rows: 9, cols: 9, mines: 10 },
  intermediate: { rows: 16, cols: 16, mines: 40 },
  expert: { rows: 16, cols: 30, mines: 99 },
};

const createBoard = (rows: number, cols: number, mines: number, firstClickRow?: number, firstClickCol?: number): Cell[][] => {
  // Initialize empty board
  const board: Cell[][] = Array(rows).fill(null).map(() =>
    Array(cols).fill(null).map(() => ({
      isMine: false,
      adjacentMines: 0,
      state: 'hidden' as CellState,
    }))
  );

  // Place mines randomly (avoiding first click if provided)
  let minesPlaced = 0;
  while (minesPlaced < mines) {
    const row = Math.floor(Math.random() * rows);
    const col = Math.floor(Math.random() * cols);

    // Skip if already a mine or if it's the first click location
    if (board[row][col].isMine) continue;
    if (firstClickRow !== undefined && firstClickCol !== undefined) {
      // Avoid placing mine on first click and adjacent cells
      if (Math.abs(row - firstClickRow) <= 1 && Math.abs(col - firstClickCol) <= 1) continue;
    }

    board[row][col].isMine = true;
    minesPlaced++;
  }

  // Calculate adjacent mines for each cell
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (board[row][col].isMine) continue;

      let count = 0;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const newRow = row + dr;
          const newCol = col + dc;
          if (newRow >= 0 && newRow < rows && newCol >= 0 && newCol < cols) {
            if (board[newRow][newCol].isMine) count++;
          }
        }
      }
      board[row][col].adjacentMines = count;
    }
  }

  return board;
};

export const Minesweeper: React.FC = () => {
  const [difficulty, setDifficulty] = useState<Difficulty>('beginner');
  const [board, setBoard] = useState<Cell[][]>(() => {
    const { rows, cols, mines } = DIFFICULTIES[difficulty];
    return createBoard(rows, cols, mines);
  });
  const [gameState, setGameState] = useState<GameState>('playing');
  const [flagCount, setFlagCount] = useState(0);
  const [time, setTime] = useState(0);
  const [isFirstClick, setIsFirstClick] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { rows, cols, mines } = DIFFICULTIES[difficulty];

  // Timer effect
  useEffect(() => {
    if (gameState === 'playing' && !isFirstClick) {
      timerRef.current = setInterval(() => {
        setTime(prev => Math.min(prev + 1, 999));
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameState, isFirstClick]);

  const resetGame = useCallback(() => {
    const { rows, cols, mines } = DIFFICULTIES[difficulty];
    setBoard(createBoard(rows, cols, mines));
    setGameState('playing');
    setFlagCount(0);
    setTime(0);
    setIsFirstClick(true);
    if (timerRef.current) clearInterval(timerRef.current);
  }, [difficulty]);

  const changeDifficulty = useCallback((newDifficulty: Difficulty) => {
    setDifficulty(newDifficulty);
    const { rows, cols, mines } = DIFFICULTIES[newDifficulty];
    setBoard(createBoard(rows, cols, mines));
    setGameState('playing');
    setFlagCount(0);
    setTime(0);
    setIsFirstClick(true);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const revealCell = useCallback((row: number, col: number) => {
    setBoard(prevBoard => {
      const newBoard = prevBoard.map(r => r.map(c => ({ ...c })));

      const reveal = (r: number, c: number) => {
        if (r < 0 || r >= rows || c < 0 || c >= cols) return;
        if (newBoard[r][c].state !== 'hidden') return;

        newBoard[r][c].state = 'revealed';

        // If no adjacent mines, reveal neighbors recursively
        if (newBoard[r][c].adjacentMines === 0 && !newBoard[r][c].isMine) {
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr !== 0 || dc !== 0) {
                reveal(r + dr, c + dc);
              }
            }
          }
        }
      };

      reveal(row, col);
      return newBoard;
    });
  }, [rows, cols]);

  const handleCellClick = useCallback((row: number, col: number) => {
    if (gameState !== 'playing') return;
    if (board[row][col].state === 'flagged' || board[row][col].state === 'question') return;
    if (board[row][col].state === 'revealed') return;

    // First click - regenerate board to ensure no mine at click location
    if (isFirstClick) {
      setIsFirstClick(false);
      const newBoard = createBoard(rows, cols, mines, row, col);

      // Reveal the clicked cell and propagate
      const reveal = (r: number, c: number) => {
        if (r < 0 || r >= rows || c < 0 || c >= cols) return;
        if (newBoard[r][c].state !== 'hidden') return;

        newBoard[r][c].state = 'revealed';

        if (newBoard[r][c].adjacentMines === 0 && !newBoard[r][c].isMine) {
          for (let dr = -1; dr <= 1; dr++) {
            for (let dc = -1; dc <= 1; dc++) {
              if (dr !== 0 || dc !== 0) {
                reveal(r + dr, c + dc);
              }
            }
          }
        }
      };

      reveal(row, col);
      setBoard(newBoard);
      return;
    }

    // Check if clicked on mine
    if (board[row][col].isMine) {
      // Game over - reveal all mines
      setBoard(prev => {
        const newBoard = prev.map(r => r.map(c => ({ ...c })));
        newBoard[row][col].state = 'revealed';
        // Reveal all mines
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            if (newBoard[r][c].isMine) {
              newBoard[r][c].state = 'revealed';
            }
          }
        }
        return newBoard;
      });
      setGameState('lost');
      return;
    }

    revealCell(row, col);
  }, [gameState, board, isFirstClick, rows, cols, mines, revealCell]);

  const handleCellRightClick = useCallback((e: React.MouseEvent, row: number, col: number) => {
    e.preventDefault();
    if (gameState !== 'playing') return;
    if (board[row][col].state === 'revealed') return;

    setBoard(prev => {
      const newBoard = prev.map(r => r.map(c => ({ ...c })));
      const cell = newBoard[row][col];

      if (cell.state === 'hidden') {
        cell.state = 'flagged';
        setFlagCount(f => f + 1);
      } else if (cell.state === 'flagged') {
        cell.state = 'question';
        setFlagCount(f => f - 1);
      } else if (cell.state === 'question') {
        cell.state = 'hidden';
      }

      return newBoard;
    });
  }, [gameState, board]);

  // Check for win condition
  useEffect(() => {
    if (gameState !== 'playing') return;

    let hiddenNonMines = 0;
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        if (!board[row][col].isMine && board[row][col].state !== 'revealed') {
          hiddenNonMines++;
        }
      }
    }

    if (hiddenNonMines === 0) {
      setGameState('won');
    }
  }, [board, gameState, rows, cols]);

  const getCellContent = (cell: Cell) => {
    if (cell.state === 'flagged') return '🚩';
    if (cell.state === 'question') return '?';
    if (cell.state === 'hidden') return '';
    if (cell.isMine) return '💣';
    if (cell.adjacentMines === 0) return '';
    return cell.adjacentMines;
  };

  const getCellClass = (cell: Cell) => {
    const classes = [styles.cell];

    if (cell.state === 'hidden' || cell.state === 'flagged' || cell.state === 'question') {
      classes.push(styles.hidden);
    } else {
      classes.push(styles.revealed);
      if (cell.isMine) {
        classes.push(styles.mine);
      } else if (cell.adjacentMines > 0) {
        classes.push(styles[`num${cell.adjacentMines}`]);
      }
    }

    return classes.join(' ');
  };

  const getFaceEmoji = () => {
    if (gameState === 'won') return '😎';
    if (gameState === 'lost') return '😵';
    return '🙂';
  };

  const formatNumber = (num: number) => {
    return num.toString().padStart(3, '0');
  };

  return (
    <div className={styles.minesweeper}>
      {/* Menu bar */}
      <div className={styles.menuBar}>
        <select
          value={difficulty}
          onChange={(e) => changeDifficulty(e.target.value as Difficulty)}
          className={styles.difficultySelect}
        >
          <option value="beginner">Beginner (9x9)</option>
          <option value="intermediate">Intermediate (16x16)</option>
          <option value="expert">Expert (16x30)</option>
        </select>
      </div>

      {/* Control panel */}
      <div className={styles.controlPanel}>
        <div className={styles.counter}>{formatNumber(mines - flagCount)}</div>
        <button className={styles.faceButton} onClick={resetGame}>
          {getFaceEmoji()}
        </button>
        <div className={styles.counter}>{formatNumber(time)}</div>
      </div>

      {/* Game board */}
      <div
        className={styles.board}
        style={{
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
        }}
      >
        {board.map((row, rowIdx) =>
          row.map((cell, colIdx) => (
            <button
              key={`${rowIdx}-${colIdx}`}
              className={getCellClass(cell)}
              onClick={() => handleCellClick(rowIdx, colIdx)}
              onContextMenu={(e) => handleCellRightClick(e, rowIdx, colIdx)}
              disabled={gameState !== 'playing' && cell.state === 'hidden'}
            >
              {getCellContent(cell)}
            </button>
          ))
        )}
      </div>

      {/* Game over overlay */}
      {gameState !== 'playing' && (
        <div className={styles.gameOverOverlay}>
          <div className={styles.gameOverMessage}>
            {gameState === 'won' ? '🎉 You Win! 🎉' : '💥 Game Over 💥'}
          </div>
          <button className={styles.playAgainButton} onClick={resetGame}>
            Play Again
          </button>
        </div>
      )}
    </div>
  );
};

export default Minesweeper;
